import { handleAsyncStatus } from 'affiny-core/modules/asynchronousState/saga';
import { EAsyncEventTypes } from 'affiny-core/modules/asynchronousState/types';
import { selectAccountId } from 'affiny-core/redux/selectors/accountSelectors';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { logger } from '../../logger';
import { navigationService } from '../../services/navigation/navigation';
import { IAccount, IOptins } from '../account/types';
import {
  deleteAccount,
  getAccount,
  getOptins,
  patchMemberAnnounce,
} from '../api';
import { EIncludedType } from '../api/models/member/member.types';
import {
  changePassword,
  getMyProfile,
  patchCityBirthdate,
  patchOptin,
  setPicturePrivacy,
} from '../api/repositories/account/account.sagas';
import { getPictures } from '../api/repositories/picture/picture.sagas';
import { ActionsOfType } from '../helpers';
import { checkHasCompletedProfile } from '../helpers/checkSaga';
import { IMember } from '../members/types';
import { IPicturesEntries } from '../pictures/types';
import { AccountActions, EAccountActionType, TAccountActions } from './actions';

export function* refreshAccountSaga() {
  const account: IAccount = yield call(getAccount);

  logger.enrichWithUser(account.id);
  yield put(AccountActions.getAccountSuccess(account));
}

export function* getAccountPicturesSaga() {
  const accountId: string = yield select(selectAccountId);
  const response: IPicturesEntries = yield call(getPictures, {
    id: accountId,
  });

  yield put(AccountActions.getPicturesAccountSuccess(response));
}

export function* getOptinsSaga() {
  const response: IOptins = yield call(getOptins);

  yield put(AccountActions.getOptinsSuccess(response));
}

export function* getMyProfileSaga() {
  const accountId: string = yield select(selectAccountId);
  const includes = [EIncludedType.ANNOUNCE];
  const response: IMember = yield call(getMyProfile, accountId, includes);

  yield put(AccountActions.getMyProfileSuccess(response));
}

export function* patchOptinSaga(
  action: ActionsOfType<TAccountActions, EAccountActionType.PATCH_OPTIN>
) {
  try {
    yield put(AccountActions.patchOptinSuccess(action.payload.newPayload));
    yield call(patchOptin, action.payload.newPayload);
  } catch (error) {
    yield put(AccountActions.patchOptinError(action.payload.initialPayload));
    throw error;
  }
}

export function* setPicturePrivacySaga(
  action: ActionsOfType<TAccountActions, EAccountActionType.SET_PICTURE_PRIVACY>
) {
  if (!(yield* checkHasCompletedProfile())) return;

  yield call(setPicturePrivacy, action.payload.privatePicture);
  yield put(AccountActions.setPicturePrivacySuccess(action.payload));
}

export function* changePasswordSaga(
  action: ActionsOfType<TAccountActions, EAccountActionType.CHANGE_PASSWORD>
) {
  if (!(yield* checkHasCompletedProfile())) return;

  yield call(changePassword, action.payload);
}

export function* patchMyAnnounceSaga(
  action: ActionsOfType<TAccountActions, EAccountActionType.PATCH_ANNOUNCE>
) {
  yield call(patchMemberAnnounce, action.payload);
  yield put(AccountActions.patchAnnounceSuccess(action.payload));
  yield call([navigationService, 'triggerSuccessModalDisplay']);
}

export function* patchCityAndBirthdateSaga(
  action: ActionsOfType<
    TAccountActions,
    EAccountActionType.PATCH_CITY_BIRTHDATE
  >
) {
  yield call(patchCityBirthdate, action.payload);
  yield put(AccountActions.patchCityBirthdateSuccess(action.payload));
}

export function* deleteAccountSaga(
  action: ActionsOfType<TAccountActions, EAccountActionType.DELETE_ACCOUNT>
) {
  try {
    yield call(deleteAccount);

    if (action.payload.resolve) action.payload.resolve();
  } catch {
    if (action.payload.reject) action.payload.reject();
  }
}

export function* accountRootSaga() {
  yield all([
    takeLatest(
      EAccountActionType.GET_OPTINS,
      handleAsyncStatus(getOptinsSaga, EAsyncEventTypes.GET_OPTINS)
    ),
    takeLatest(
      EAccountActionType.PATCH_OPTIN,
      handleAsyncStatus(patchOptinSaga, EAsyncEventTypes.PATCH_OPTINS)
    ),
    takeLatest(
      EAccountActionType.GET_PROFILE,
      handleAsyncStatus(
        getMyProfileSaga,
        EAsyncEventTypes.GET_MY_PROFILE,
        false
      )
    ),
    takeLatest(
      EAccountActionType.GET_PICTURES,
      handleAsyncStatus(
        getAccountPicturesSaga,
        EAsyncEventTypes.GET_ACCOUNT_PICTURES
      )
    ),
    takeLatest(
      EAccountActionType.PATCH_ANNOUNCE,
      handleAsyncStatus(patchMyAnnounceSaga, EAsyncEventTypes.PATCH_MY_ANNOUNCE)
    ),
    takeLatest(
      EAccountActionType.DELETE_ACCOUNT,
      handleAsyncStatus(deleteAccountSaga, EAsyncEventTypes.DELETE_ACCOUNT)
    ),
    takeLatest(
      EAccountActionType.PATCH_CITY_BIRTHDATE,
      handleAsyncStatus(
        patchCityAndBirthdateSaga,
        EAsyncEventTypes.PATCH_CITY_BIRTHDATE
      )
    ),
  ]);
}
